import { useState } from "react";
import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Paper, Stack, TextField, Typography,
} from "@mui/material";
import { easternInputToUtcIso, utcIsoToEasternInputValue } from "../utils/timezone";
import { isWishlistLocked } from "../utils/countdown";
import {
  useSetWishlistDueDate, useClearWishlistDueDate,
  useSetGiftExchangeDate, useClearGiftExchangeDate,
  useSetWishlistUnlockOverride, useRegenerateJoinCode,
} from "../hooks/useGroups";
import { useSubmitRequest } from "../hooks/useGroupRequests";
import GoHomeButton from "./GoHomeButton";

const MAX_INVITE_EMAILS = 25;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseInviteEmails(text) {
  const seen = new Set();
  const emails = [];
  for (const raw of text.split(/[\n,]/)) {
    const email = raw.trim().toLowerCase();
    if (!email || seen.has(email)) continue;
    seen.add(email);
    emails.push(email);
  }
  return emails;
}

const FIELD_CONFIG = {
  due: {
    column: "wishlist_due_date",
    label: "Wishlist Due-by Date",
    description:
      "Members will see a countdown to this date in the group banner, and their wishlists lock from further edits once it passes.",
  },
  exchange: {
    column: "gift_exchange_date",
    label: "Gift Exchange Date",
    description: "The date and time of your group's gift exchange or party.",
  },
};

function formatEastern(isoString) {
  if (!isoString) return null;
  return (
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(isoString)) + " ET"
  );
}

export default function LeaderActions({
  activeGroupId,
  userId,
  wishlistDueDate,
  giftExchangeDate,
  wishlistUnlockedOverride,
  joinCode,
  onNavigateHome,
}) {
  const setDueDateMutation = useSetWishlistDueDate();
  const clearDueDateMutation = useClearWishlistDueDate();
  const setExchangeDateMutation = useSetGiftExchangeDate();
  const clearExchangeDateMutation = useClearGiftExchangeDate();
  const setUnlockOverrideMutation = useSetWishlistUnlockOverride();
  const regenerateJoinCodeMutation = useRegenerateJoinCode();
  const submitRequestMutation = useSubmitRequest();

  const [editingField, setEditingField] = useState(null); // "due" | "exchange" | null
  const [draftValue, setDraftValue] = useState("");
  const [fieldError, setFieldError] = useState(null);
  const [savingField, setSavingField] = useState(null);
  const [savingOverride, setSavingOverride] = useState(false);
  const [status, setStatus] = useState(null);

  const [inviteExpanded, setInviteExpanded] = useState(false);
  const [inviteText, setInviteText] = useState("");
  const [inviteError, setInviteError] = useState(null);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteSuccessMsg, setInviteSuccessMsg] = useState(null);

  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [regenerateError, setRegenerateError] = useState(null);

  const currentValue = { due: wishlistDueDate, exchange: giftExchangeDate };

  const startEditing = (field) => {
    setEditingField(field);
    setDraftValue(utcIsoToEasternInputValue(currentValue[field]));
    setFieldError(null);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setDraftValue("");
    setFieldError(null);
  };

  const saveField = async (field) => {
    if (!draftValue) {
      setFieldError("Please choose a date and time.");
      return;
    }
    const utcIso = easternInputToUtcIso(draftValue);
    if (new Date(utcIso).getTime() <= Date.now()) {
      setFieldError("Must be a date and time in the future.");
      return;
    }

    setSavingField(field);
    setFieldError(null);

    try {
      if (field === "due") {
        await setDueDateMutation.mutateAsync({ groupId: activeGroupId, dueDate: utcIso });
      } else {
        await setExchangeDateMutation.mutateAsync({ groupId: activeGroupId, exchangeDate: utcIso });
      }
    } catch {
      setFieldError("Failed to save. Please try again.");
      setSavingField(null);
      return;
    }

    setSavingField(null);
    setEditingField(null);
    setDraftValue("");
  };

  const clearField = async (field) => {
    setSavingField(field);

    try {
      if (field === "due") {
        await clearDueDateMutation.mutateAsync({ groupId: activeGroupId });
      } else {
        await clearExchangeDateMutation.mutateAsync({ groupId: activeGroupId });
      }
    } catch {
      setStatus({ type: "error", msg: "Failed to clear. Please try again." });
      setSavingField(null);
      return;
    }

    setSavingField(null);
  };

  const toggleOverride = async () => {
    setSavingOverride(true);
    const nextOverride = !wishlistUnlockedOverride;

    try {
      await setUnlockOverrideMutation.mutateAsync({ groupId: activeGroupId, unlocked: nextOverride });
    } catch {
      setStatus({ type: "error", msg: "Failed to update. Please try again." });
      setSavingOverride(false);
      return;
    }

    setSavingOverride(false);
  };

  const startInvite = () => {
    setInviteExpanded(true);
    setInviteText("");
    setInviteError(null);
    setInviteSuccessMsg(null);
  };

  const cancelInvite = () => {
    setInviteExpanded(false);
    setInviteText("");
    setInviteError(null);
  };

  const submitInvite = async () => {
    const emails = parseInviteEmails(inviteText);

    if (emails.length === 0) {
      setInviteError("Please enter at least one email address.");
      return;
    }
    if (emails.length > MAX_INVITE_EMAILS) {
      setInviteError(`Please submit ${MAX_INVITE_EMAILS} or fewer emails at a time.`);
      return;
    }
    const invalid = emails.filter((email) => !EMAIL_REGEX.test(email));
    if (invalid.length > 0) {
      setInviteError(`Invalid email address${invalid.length > 1 ? "es" : ""}: ${invalid.join(", ")}`);
      return;
    }

    setInviteSubmitting(true);
    setInviteError(null);

    try {
      await submitRequestMutation.mutateAsync({ fromUserId: userId, requestType: "bulk_invite", groupId: activeGroupId, invitedEmails: emails });
    } catch {
      setInviteSubmitting(false);
      setInviteError("Failed to submit invite request. Please try again.");
      return;
    }

    setInviteSubmitting(false);
    setInviteExpanded(false);
    setInviteText("");
    setInviteSuccessMsg(
      `Invite request submitted for ${emails.length} email${emails.length > 1 ? "s" : ""}. An admin will review it before invites go out.`
    );
  };

  const copyJoinCode = async () => {
    await navigator.clipboard.writeText(joinCode);
    setCopyConfirmed(true);
    setTimeout(() => setCopyConfirmed(false), 2000);
  };

  const regenerateJoinCode = async () => {
    setRegenerateError(null);
    try {
      await regenerateJoinCodeMutation.mutateAsync({ groupId: activeGroupId });
      setConfirmRegenerate(false);
    } catch {
      setRegenerateError("Failed to regenerate the join code. Please try again.");
    }
  };

  const renderDateCard = (field) => {
    const config = FIELD_CONFIG[field];
    const value = currentValue[field];
    const isEditing = editingField === field;

    return (
      <Paper key={field} variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {config.label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {config.description}
        </Typography>

        {isEditing ? (
          <Stack spacing={1}>
            <TextField
              type="datetime-local"
              label="Date & time"
              value={draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Eastern Time"
              size="small"
            />
            {fieldError && <Alert severity="error">{fieldError}</Alert>}
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={() => saveField(field)} disabled={savingField === field}>
                {savingField === field ? "Saving..." : "Save"}
              </Button>
              <Button onClick={cancelEditing} disabled={savingField === field}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap">
            {value ? (
              <Typography sx={{ fontWeight: 600 }}>{formatEastern(value)}</Typography>
            ) : (
              <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
                Not set
              </Typography>
            )}
            <Stack direction="row" spacing={1}>
              <Button size="small" variant={value ? "outlined" : "contained"} onClick={() => startEditing(field)}>
                {value ? "Edit" : "Set Date"}
              </Button>
              {value && (
                <Button size="small" onClick={() => clearField(field)} disabled={savingField === field}>
                  {savingField === field ? "Clearing..." : "Clear"}
                </Button>
              )}
            </Stack>
          </Stack>
        )}
      </Paper>
    );
  };

  const locked = isWishlistLocked(wishlistDueDate, wishlistUnlockedOverride, new Date());

  return (
    <Paper className="page-card" sx={{ p: 3, mb: 4 }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <GoHomeButton onNavigateHome={onNavigateHome} />
      </Stack>

      {status && (
        <Alert severity={status.type} sx={{ mb: 2 }}>
          {status.msg}
        </Alert>
      )}

      <Stack spacing={2}>
        {renderDateCard("due")}
        {renderDateCard("exchange")}

        {wishlistDueDate && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Wishlist Editing
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Once the Due-by Date passes, members can no longer add, edit, or delete wishlist items.
              Claiming and purchasing are unaffected. Use this to temporarily reopen editing without
              changing the Due-by Date.
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap">
              <Chip
                label={wishlistUnlockedOverride ? "Unlocked (override)" : locked ? "Locked (auto)" : "Unlocked"}
                color={wishlistUnlockedOverride ? "warning" : locked ? "default" : "success"}
              />
              {(locked || wishlistUnlockedOverride) && (
                <Button size="small" variant="contained" onClick={toggleOverride} disabled={savingOverride}>
                  {savingOverride ? "Saving..." : wishlistUnlockedOverride ? "Re-lock" : "Unlock Editing"}
                </Button>
              )}
            </Stack>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Group Join Code
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Share this code with someone you want in your group — they enter it when they
            sign up and are added right away, no approval needed.
          </Typography>

          {regenerateError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {regenerateError}
            </Alert>
          )}

          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap">
            <Typography sx={{ fontWeight: 700, fontSize: "1.5rem", letterSpacing: "0.3em" }}>
              {joinCode || "------"}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={copyJoinCode} disabled={!joinCode}>
                {copyConfirmed ? "Copied!" : "Copy"}
              </Button>
              <Button size="small" onClick={() => setConfirmRegenerate(true)} disabled={!joinCode}>
                Regenerate
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Invite Members
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Submit a list of email addresses to invite to this group. An admin must approve the
            request before invite emails are sent.
          </Typography>

          {inviteSuccessMsg && (
            <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setInviteSuccessMsg(null)}>
              {inviteSuccessMsg}
            </Alert>
          )}

          {inviteExpanded ? (
            <Stack spacing={1}>
              <TextField
                multiline
                minRows={3}
                label="Email addresses"
                placeholder="one@example.com, two@example.com&#10;three@example.com"
                value={inviteText}
                onChange={(e) => setInviteText(e.target.value)}
                helperText="Separate with commas or new lines"
                size="small"
              />
              {inviteError && <Alert severity="error">{inviteError}</Alert>}
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={submitInvite} disabled={inviteSubmitting}>
                  {inviteSubmitting ? "Submitting..." : "Send Invite Request"}
                </Button>
                <Button onClick={cancelInvite} disabled={inviteSubmitting}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Button size="small" variant="contained" onClick={startInvite}>
              Invite Members
            </Button>
          )}
        </Paper>
      </Stack>

      <Dialog open={confirmRegenerate} onClose={() => setConfirmRegenerate(false)}>
        <DialogTitle>Regenerate Join Code</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Generate a new join code for this group? The current code ({joinCode}) will stop
            working immediately — anyone who has it but hasn't joined yet will need the new one.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRegenerate(false)} disabled={regenerateJoinCodeMutation.isPending}>
            Cancel
          </Button>
          <Button color="error" onClick={regenerateJoinCode} disabled={regenerateJoinCodeMutation.isPending}>
            {regenerateJoinCodeMutation.isPending ? "Regenerating..." : "Regenerate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
